<template>
  <!--
    工作流列表：以「流程实例」为查询维度看全部数据。
    与审批中心的差别只在维度 —— 审批中心查的是「指派给我的待办」，
    这里查的是「这个商户跑过的所有流程」，不按处理人过滤。
  -->
  <div class="workflow-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="workflowList">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="事项标题/工作流号" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.businessType" placeholder="全部" clearable @change="resetSearch">
            <template #prefix>流程类型</template>
            <el-option v-for="t in templateList" :key="t.businessType" :label="t.businessName" :value="t.businessType"/>
          </el-select>
          <yq-date-picker v-model="searchInfo.startDateBegin" type="date" value-format="YYYY-MM-DD"
                          placeholder="发起时间起" @change="resetSearch"/>
          <yq-date-picker v-model="searchInfo.startDateEnd" type="date" value-format="YYYY-MM-DD"
                          placeholder="发起时间止" @change="resetSearch"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <!--
        ★ 这里原本多套了一层 <list-table>（现已删除），它把 KPI 卡片、说明条、
          两张表格、分页条全包在里面，是个多余的包裹层，且会引发两个问题：
            1. ListPage 对直接子级 .list-table 有 `flex: 1; height: 50%` 的默认规则，
               这层因而被压成「剩余空间」，表格塞进去后溢出，表头下方冒出滚动条；
               内层那句 height="auto" 也被它盖住而失效。
            2. 页面上会同时存在两层 .list-table 嵌套，排查时极易误判。
          删掉后各区块直接挂在 .list-page 下，由页面外壳统一滚动。

        ★ 不要用一个 <div> 把原来那些内容再包回去：那样它就成了 .list-page 的唯一
          flex 子项，既不匹配上面那条 `>.list-table` 规则、又会整体被压扁，
          反而把搜索区挤掉（踩过）。
      -->
      <!-- 工作流统计：全商户口径 -->
      <div class="stat-grid">
          <div class="stat-card">
            <div class="label">进行中</div>
            <div class="value" style="color:#e6a23c">{{ stat.runningCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">审批中与待重新提交</div>
          </div>
          <div class="stat-card">
            <div class="label">已完成</div>
            <div class="value" style="color:#67c23a">{{ stat.finishedCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">审批走完全部节点</div>
          </div>
          <div class="stat-card">
            <div class="label">已中止</div>
            <div class="value" style="color:#f56c6c">{{ stat.suspendedCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">被审批人驳回或叫停</div>
          </div>
          <div class="stat-card">
            <div class="label">已撤回</div>
            <div class="value" style="color:#909399">{{ stat.withdrawnCount || 0 }}<span class="unit">条</span></div>
            <div class="sub">业务收回，退回开始节点待重提</div>
          </div>
        </div>
        <div class="tip-bar">
          统计口径：本页按<b>工作流实例</b>统计全商户数据，不限于当前登录人。
          与审批中心的口径不同（那边只算「与我相关」的），两个页面的数字对不上是正常的。
        </div>

        <!-- 工作流列表。点击任意一行从右侧滑出该流程的审批节点 drawer -->
        <div class="section-title">工作流程</div>
        <!--
          工作流列表：不写 height="auto"，走 ListPage 的默认布局 ——
          表格占满剩余高度、内部滚动、表头吸顶。

          ★ 为什么不用 height="auto"：那要求表格按内容撑开、由外层容器滚动，
            而 ListPage 是 flex 布局，直接用子级 `.list-table { flex: 1; height: 50% }`
            把这块压成「剩余空间」，两者天生冲突（反复踩过）。
            本页内容多（KPI + 说明条 + 两张表 + 分页条），用默认布局更合适。
        -->
        <list-table v-loading="loading" background-color="#FFF">
          <yq-table :data="dataList" :empty-text="'暂无工作流'"
                    :highlight-current-row="true" row-key="id" :current-row-key="currentInstanceId"
                    @rowClick="onRowClick">
            <yq-table-column label="工作流ID" prop="id" width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}"><span class="num muted code-text">{{ row.id }}</span></template>
            </yq-table-column>
            <yq-table-column label="审批事项" prop="bizTitle" min-width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <div class="cell-title">{{ row.bizTitle || '—' }}</div>
                <div v-if="row.processName" class="muted code-text">{{ row.processName }}</div>
              </template>
            </yq-table-column>
            <yq-table-column label="流程类型" prop="businessType" width="120rem" align="center">
              <template v-slot="{row}">
                <el-tag v-if="row.businessName" size="small" type="info">{{ row.businessName }}</el-tag>
                <span v-else>{{ row.businessType || '—' }}</span>
              </template>
            </yq-table-column>
            <yq-table-column label="状态" prop="instanceStatus" width="100rem" align="center">
              <template v-slot="{row}">
                <el-tag size="small" :type="statusGroupTag(row.instanceStatus)">
                  {{ statusGroupName(row.instanceStatus) }}
                </el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="发起人" prop="startUserName" width="100rem">
              <template v-slot="{row}">{{ row.startUserName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="提交时间" prop="startTime" width="165rem">
              <template v-slot="{row}"><span class="num muted">{{ fmtTime(row.startTime) }}</span></template>
            </yq-table-column>
            <yq-table-column label="当前节点" prop="currentNodeNames" width="150rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <span v-if="row.currentNodeNames">{{ row.currentNodeNames }}</span>
                <span v-else class="muted">{{ endNodeText(row) }}</span>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>
        <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                   :total-count="searchInfo.totalCount" @onLoad="queryList"/>

    </list-page>

    <!--
      审批节点信息：点击列表行后从右侧滑出。
      放 drawer 而不是页面下方，是因为节点表会在主页面里按内容撑开，
      把整页顶出第二条滚动条；抽屉自己有独立的滚动区域，不与列表抢。
    -->
    <el-drawer v-model="showNodeDrawer" :title="drawerTitle" size="60%" :destroy-on-close="true">
      <div v-loading="detailLoading" class="node-drawer">
        <div v-if="!detail && !detailLoading" class="empty-block">
          <yq-icon name="element-ui_info"/>
          <span>未找到该流程的节点信息</span>
        </div>
        <template v-else-if="detail">
          <div class="instance-bar">
            <span class="bar-item">工作流ID：<b class="num code-text">{{ detail.id }}</b></span>
            <span class="bar-item">流程：<b>{{ detail.processName || '—' }}</b></span>
            <span class="bar-item">状态：
              <el-tag size="small" :type="statusGroupTag(detail.instanceStatus)">
                {{ detail.instanceStatusName || '—' }}
              </el-tag>
            </span>
            <span class="bar-item">当前节点：<b>{{ detail.currentNodeNames || '—' }}</b></span>
          </div>
          <!--
            节点：待办里的活动节点 + 轨迹里出现过的节点，按流程顺序合并展示。
            节点数很少（几个到十几个），height="auto" 按内容展开即可。
            ⚠ 不要改成 420rem 这类值：height 是直接落到 CSS 变量 --height 上的，
              不经过 px2rem 转换，rem 会按页面根字号（视口/10）解析，算出来是天文数字。
          -->
          <yq-table :data="nodeRows" :empty-text="'该流程尚未产生审批节点'" height="auto">
            <yq-table-column label="节点" prop="nodeName" min-width="160rem">
              <template v-slot="{row}"><span class="cell-title">{{ row.nodeName }}</span></template>
            </yq-table-column>
            <yq-table-column label="处理人" prop="assignees" min-width="140rem">
              <template v-slot="{row}">
                <span v-if="row.assignees">{{ row.assignees }}</span>
                <span v-else class="muted">—</span>
              </template>
            </yq-table-column>
            <yq-table-column label="节点状态" prop="state" width="110rem" align="center">
              <template v-slot="{row}">
                <el-tag size="small" :type="nodeStateTag(row.state)">{{ row.state }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="处理结果" prop="resultText" min-width="180rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">{{ row.resultText || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="处理时间" prop="handleTime" width="165rem">
              <template v-slot="{row}">
                <span class="num muted">{{ row.handleTime ? fmtTime(row.handleTime) : '—' }}</span>
              </template>
            </yq-table-column>
          </yq-table>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import YqDatePicker from "@/components/h5/date/YqDatePicker.vue";
import YqIcon from "@/components/YqIcon.vue";
import checkApi from "@/api/dz-project/project/checkApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

/** 状态分组，与后端 ProcessFlowConstant.StatusGroup 对齐 */
const STATUS_GROUPS = [
  {value: 100, label: "进行中"},
  {value: 200, label: "已完成"},
  {value: 300, label: "已中止"},
  {value: 400, label: "已撤回"},
];
/**
 * 实例状态 -> 分组。
 * 与后端 ProcessFlowConstant.StatusGroup.statusesOf 严格对应：
 *   已撤销(300)/已驳回(400) 归「已中止」——都是审批终止，单据到此为止
 *   已撤回(500)             单独归「已撤回」——业务收回，还会重新提交
 * 这两者不能混：混了会让工作流列表把撤回的单据显示成已中止，
 * 与业务列表显示的「已撤回」对不上。
 */
const INSTANCE_STATUS = {0: 100, 100: 100, 140: 100, 200: 200, 300: 300, 400: 300, 500: 400};
/** 任务状态，与后端 ProcessFlowConstant.TaskStatus 对齐 */
const TASK_STATUS = {0: "待处理", 100: "处理中", 200: "已处理", 300: "已取消"};
/** 任务结果，与后端 ProcessFlowConstant.TaskResult 对齐 */
const TASK_RESULT = {100: "通过", 200: "驳回"};
/** 轨迹动作，与后端 ProcessFlowConstant.Action 对齐。这里只用到判定「是不是派发」 */
const ACTION = {CLAIM: 240};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "WorkflowList",
  components: {
    YqIcon, YqDatePicker, YqTableColumn, YqTable, PageButton,
    FormPage, SearchCondition, ListTable, ListPage,
  },
  data() {
    return {
      searchInfo: {
        searchKey: "",
        businessType: "",
        statusGroup: "",
        startDateBegin: "",
        startDateEnd: "",
        pageNumber: 1,
        pageSize: 20,
        totalCount: 0,
      },
      stat: {},
      dataList: [],
      templateList: [],
      loading: false,
      currentInstanceId: "",
      detail: null,
      // 审批节点 drawer
      showNodeDrawer: false,
      detailLoading: false,
    };
  },
  computed: {
    /** 抽屉标题：带上事项标题，多开几次也能分清看的是哪一条 */
    drawerTitle() {
      const title = this.detail && this.detail.bizTitle;
      return title ? "审批节点信息 · " + title : "审批节点信息";
    },
    /**
     * 审批节点信息。
     *
     * <p>数据来自实例详情的两部分：taskList 是当前还挂着的待办，
     * logList 是已经走过的轨迹。以轨迹里出现过的节点顺序为骨架，
     * 把待办补进去 —— 只展示轨迹会漏掉「正在等谁处理」，只展示待办会漏掉历史。
     */
    nodeRows() {
      if (!this.detail) {
        return [];
      }
      const rows = [];
      const index = {};
      const push = (nodeId, nodeName) => {
        const key = nodeId || nodeName || "unknown";
        if (index[key] !== undefined) {
          return rows[index[key]];
        }
        const row = {nodeId, nodeName: nodeName || "—", assignees: "", checkType: null, state: "", resultText: "", handleTime: null};
        index[key] = rows.length;
        rows.push(row);
        return row;
      };
      // 已处理过的节点：从轨迹里还原
      (this.detail.logList || []).forEach(log => {
        if (!log.nodeId) {
          return;
        }
        const row = push(log.nodeId, log.nodeName);
        // 派发(240)只说明「这个节点派给了谁」，不是处理结果。
        // 它带来的是处理人信息，结果文案要留给真正的审批动作（通过/驳回）去填 ——
        // 否则节点会显示成「派发：节点[X]待[张三]审批」，看不出经理到底批没批
        if (log.action === ACTION.CLAIM) {
          if (log.operatorName && !row.assignees.includes(log.operatorName)) {
            row.assignees = row.assignees ? row.assignees + "、" + log.operatorName : log.operatorName;
          }
          if (!row.handleTime) {
            row.handleTime = log.createdTime;
          }
          if (!row.state) {
            row.state = "已派发";
          }
          return;
        }
        if (log.operatorName && !row.assignees.includes(log.operatorName)) {
          row.assignees = row.assignees ? row.assignees + "、" + log.operatorName : log.operatorName;
        }
        if (log.actionName) {
          // 通过/驳回这类动作把意见一并显示，这才是「处理结果」
          row.resultText = log.opinion && !log.opinion.startsWith(log.actionName)
            ? log.actionName + "：" + log.opinion
            : (log.opinion || log.actionName);
        }
        row.handleTime = log.createdTime || row.handleTime;
        if (!row.state || row.state === "已派发") {
          row.state = "已处理";
        }
      });
      // 当前待办：覆盖节点状态为待处理，并补上签署方式
      (this.detail.taskList || []).forEach(task => {
        const row = push(task.nodeId, task.nodeName);
        if (task.assigneeName) {
          row.assignees = row.assignees ? row.assignees + "、" + task.assigneeName : task.assigneeName;
        }
        // 抄送任务落成待处理，节点状态要说清是「等确认」而不是「等审批」，
        // 否则看流程的人会以为抄送节点也需要审批
        row.state = task.taskTypeName === "待确认"
          ? "待确认"
          : (TASK_STATUS[task.taskStatus] || "待处理");
        if (task.taskResult) {
          row.resultText = TASK_RESULT[task.taskResult] || row.resultText;
        }
        if (task.completeTime) {
          row.handleTime = task.completeTime;
        }
      });
      return rows;
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    // 从审批中心点「全部」过来时会带上当前的流程类型筛选，接着用，不用重选
    this.searchInfo.businessType = this.route.query.businessType || "";
    this.initTemplate();
    this.loadStat();
    this.queryList();
  },
  methods: {
    async initTemplate() {
      const list = await commonApi.queryBusinessTemplateList();
      this.templateList = list || [];
    },
    async loadStat() {
      const data = await checkApi.queryWorkflowStat({businessType: this.searchInfo.businessType});
      this.stat = data || {};
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.currentInstanceId = "";
      // 重新查询后原来选中那行多半已不在当前页，抽屉里的内容也就失效了，一并关掉
      this.detail = null;
      this.showNodeDrawer = false;
      this.loadStat();
      this.queryList();
    },
    async queryList() {
      this.loading = true;
      try {
        const data = await checkApi.queryWorkflowList({
          searchKey: this.searchInfo.searchKey,
          businessType: this.searchInfo.businessType,
          statusGroup: this.searchInfo.statusGroup,
          startDateBegin: this.searchInfo.startDateBegin,
          startDateEnd: this.searchInfo.startDateEnd,
          pageNumber: this.searchInfo.pageNumber,
          pageSize: this.searchInfo.pageSize,
        });
        // PageBean 的字段是 records / totalCount
        this.dataList = (data && data.records) || [];
        this.searchInfo.totalCount = (data && data.totalCount) || 0;
      } finally {
        this.loading = false;
      }
    },
    /** 点击某行：打开抽屉并加载该流程的实例详情 */
    async onRowClick(row) {
      this.currentInstanceId = row.id;
      this.showNodeDrawer = true;
      this.detail = null;
      this.detailLoading = true;
      try {
        const detail = await checkApi.findInstanceDetail(row.id);
        this.detail = detail || null;
      } catch (e) {
        this.detail = null;
      } finally {
        this.detailLoading = false;
      }
    },
    /** 实例状态归到三档展示 */
    statusGroupName(instanceStatus) {
      const group = INSTANCE_STATUS[instanceStatus];
      return group ? (STATUS_GROUPS.find(g => g.value === group) || {}).label || "—" : "—";
    },
    statusGroupTag(instanceStatus) {
      const group = INSTANCE_STATUS[instanceStatus];
      if (group === 100) {
        return "warning";
      }
      if (group === 200) {
        return "success";
      }
      if (group === 300) {
        return "danger";
      }
      if (group === 400) {
        // 已撤回用灰：不是「出了事」，只是业务收回去了
        return "info";
      }
      return "info";
    },
    nodeStateTag(state) {
      if (state === "已处理") {
        return "success";
      }
      // 待确认用 info、待处理用 warning：前者只是通知待阅，不紧急
      if (state === "已取消" || state === "待确认") {
        return "info";
      }
      return "warning";
    },
    /** 终态流程没有「当前节点」，按状态给个更贴切的说明 */
    endNodeText(row) {
      if (row.instanceStatus === 200) {
        return "已结束";
      }
      if (row.instanceStatus === 400) {
        return "已驳回";
      }
      if (row.instanceStatus === 300) {
        return "已撤回";
      }
      return "—";
    },
    fmtTime(v) {
      if (!v) {
        return "—";
      }
      return String(v).replace("T", " ").slice(0, 16);
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow-list {
  height: 100%;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 14px 0 10px;

  .section-sub {
    font-size: 12px;
    font-weight: 400;
    color: #909399;
    margin-left: 8px;
  }
}

/*
  审批节点抽屉。
  ★ 自带 padding 与撑满高度：el-drawer 的 body 默认没有内边距，
    不补的话内容会贴着抽屉边缘，看着像没加载完。
*/
.node-drawer {
  height: 100%;
  padding: 16px;
  box-sizing: border-box;
  overflow: auto;
}

.instance-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: #606266;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  margin-bottom: 10px;

  .bar-item b {
    color: #303133;
    font-weight: 600;
  }
}

.empty-block {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #909399;
  font-size: 13px;
  background: #fafbfc;
  border: 1px dashed var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 20px 14px;
  justify-content: center;
}

.num {
  font-variant-numeric: tabular-nums;
}

.muted {
  color: #909399;
}

.cell-title {
  color: #303133;
  font-weight: 500;
}

.code-text {
  font-family: Consolas, Menlo, monospace;
  font-size: 11px;
}
</style>
